const BFF_BASE_URL = process.env.VUE_APP_BFF_URL || 'https://localhost:44301'

const initialState = () => ({
  isPro: false,
  subscriptionLoaded: false,
  aiUsage: null,
  checkoutLoading: false,
})

export const state = initialState()

export const getters = {
  isPro: (s) => s.isPro,
  subscriptionLoaded: (s) => s.subscriptionLoaded,
  aiUsage: (s) => s.aiUsage,
  checkoutLoading: (s) => s.checkoutLoading,
}

export const actions = {
  async fetchAiUsage({ commit }) {
    try {
      const res = await fetch(`${BFF_BASE_URL}/v1/payment/ai-usage`, {
        credentials: 'include',
        headers: { 'X-CSRF': '1' },
      })
      if (res.ok) {
        const data = await res.json()
        commit('SET_AI_USAGE', data)
      }
    } catch (err) {
      console.error('Error fetching AI usage:', err)
    }
  },

  async fetchSubscription({ commit }) {
    try {
      const res = await fetch(`${BFF_BASE_URL}/v1/payment/subscription`, {
        credentials: 'include',
        headers: { 'X-CSRF': '1' },
      })
      if (res.ok) {
        const data = await res.json()
        // busy-main-bff returns plan/status capitalised (e.g. "Pro"/"Active"); compare
        // case-insensitively so casing on the API side can't dormantly break this getter.
        commit('SET_IS_PRO', data?.plan?.toLowerCase() === 'pro' && data?.status?.toLowerCase() === 'active')
      }
    } catch (err) {
      console.error('Error fetching subscription:', err)
    } finally {
      commit('SET_SUBSCRIPTION_LOADED', true)
    }
  },

  // Private owner cheat code: POST the secret to the BFF, which validates it and grants Pro
  // server-side (durable dev_pro flag → subscription claims). Reflect it locally so the UI
  // (crown, upgrade prompts) updates immediately; server enforcement follows on the next request.
  async redeemProCode({ commit }, code) {
    try {
      const res = await fetch(`${BFF_BASE_URL}/v1/payment/dev-pro`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF': '1' },
        body: JSON.stringify({ code }),
      })
      if (res.ok) {
        commit('SET_IS_PRO', true)
        return true
      }
      return false
    } catch (err) {
      console.error('redeemProCode error:', err)
      return false
    }
  },

  async checkout({ commit }, { interval }) {
    commit('SET_CHECKOUT_LOADING', true)
    try {
      const res = await fetch(`${BFF_BASE_URL}/v1/payment/checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF': '1' },
        body: JSON.stringify({
          plan: 'pro',
          interval,
          // Our own origin: the success page lives in this app now (the marketing site that
          // used to host it is retired), and origin-relative keeps it correct on both
          // my.busy.me and busy.me.
          successUrl: `${window.location.origin}/payment-success`,
          cancelUrl: window.location.href,
        }),
      })

      if (res.status === 401) {
        window.location.href = `${BFF_BASE_URL}/bff/login?returnUrl=${encodeURIComponent(window.location.href)}`
        return
      }
      if (!res.ok) {
        const d = await res.json()
        throw new Error(d.error || 'Failed to start checkout')
      }
      const data = await res.json()
      const url = data?.url || data?.Url
      if (url) window.location.href = url
    } catch (err) {
      console.error('Checkout error:', err)
      throw err
    } finally {
      commit('SET_CHECKOUT_LOADING', false)
    }
  },
}

export const mutations = {
  SET_IS_PRO(s, val) {
    s.isPro = val
  },
  SET_SUBSCRIPTION_LOADED(s, val) {
    s.subscriptionLoaded = val
  },
  SET_AI_USAGE(s, val) {
    s.aiUsage = val
  },
  SET_CHECKOUT_LOADING(s, val) {
    s.checkoutLoading = val
  },
}

export default {
  state,
  getters,
  actions,
  mutations,
}
